import { v4 as uuid } from "uuid";
import type {
    LomdaScene,
    LomdaSceneAvatar,
    SceneInteraction,
} from "@/features/lomdaEditorV2/lib/lomdaTypes";
import {
    flattenChapterSceneIds,
    insertSceneAfter,
    isolateSceneAsChapter as isolateSceneAsChapterUtil,
    moveSceneToChapter as moveSceneToChapterUtil,
    moveSceneToNextChapter as moveSceneToNextChapterUtil,
    moveSceneToPreviousChapter as moveSceneToPreviousChapterUtil,
    normalizeChapters,
    removeSceneFromAllChapters,
    renameChapter as renameChapterUtil,
    reorderChapters as reorderChaptersUtil,
    splitAt as splitAtUtil,
} from "@/features/lomdaEditorV2/lib/chapterUtils";
import {
    directionOf,
    ensureSceneElements,
} from "@/features/lomdaEditorV2/lib/elements/migrateScene";
import {
    createAvatarElement,
    defaultAvatarTransform,
} from "@/features/lomdaEditorV2/lib/elements/factories";
import { FULLSCREEN_BOX } from "@/features/lomdaEditorV2/lib/elements/transform";
import {
    mirrorSlotsForRtl,
    tileRegion,
} from "@/features/lomdaEditorV2/lib/elements/layouts/apply";
import { setContentAlignment } from "@/features/lomdaEditorV2/lib/sceneContentBlocks";
import {
    isMediaElement,
    type ElementTransform,
    type SceneElement,
} from "@/features/lomdaEditorV2/lib/elements/types";
import type {
    DocumentSlice,
    EditorGet,
    EditorSet,
    GapId,
} from "../types";

const renumber = (items: SceneInteraction[]): SceneInteraction[] =>
    items.map((it, i) => (it.order === i ? it : { ...it, order: i }));

/**
 * Floating-layout snapshots, captured per avatar element when it goes
 * full-screen so the toggle can restore the exact prior box/style/order.
 * Transient (not persisted) — on a cold load with no snapshot we fall back to
 * the default floating preset instead.
 */
const avatarFloatSnapshots = new Map<
    string,
    { transform: ElementTransform; avatar: LomdaSceneAvatar }
>();

/** Rebuild scenes order to match the flattened chapter order. */
function resortScenesByChapters(state: {
    scenes: LomdaScene[];
    chapters: { sceneIds: string[] }[];
}) {
    const order = new Map<string, number>();
    flattenChapterSceneIds(state.chapters as never).forEach((id, i) =>
        order.set(id, i)
    );
    state.scenes = [...state.scenes]
        .sort((a, b) => (order.get(a.id) ?? Infinity) - (order.get(b.id) ?? Infinity))
        .map((s, i) => (s.order === i ? s : { ...s, order: i }));
}

function findScene(scenes: LomdaScene[], sceneId: string) {
    return scenes.find((s) => s.id === sceneId);
}

function getElements(scene: LomdaScene): SceneElement[] {
    if (!scene.elements) scene.elements = ensureSceneElements(scene).elements!;
    return scene.elements;
}

export function createDocumentSlice(
    set: EditorSet,
    get: EditorGet
): DocumentSlice {
    return {
        scenes: [],
        chapters: [],
        metadata: {} as never,
        lomdaName: "",

        addScene: (sceneData) => {
            set((state) => {
                const newScene: LomdaScene = {
                    ...(sceneData as LomdaScene),
                    id: uuid(),
                    order: state.scenes.length,
                    elements: [],
                };
                state.scenes.push(newScene);
                const last = state.chapters[state.chapters.length - 1];
                if (last) last.sceneIds.push(newScene.id);
                state.chapters = normalizeChapters(state.scenes, state.chapters);
                state.metadata.numOfScenes = state.scenes.length;
                state.selectedSceneId = newScene.id;
                state.selection = { kind: "none" };
            });
        },

        updateScene: (sceneId, updates) => {
            set((state) => {
                const scene = findScene(state.scenes, sceneId);
                if (!scene) return;
                if (updates.style !== undefined) {
                    const merged = { ...(scene.style ?? {}), ...updates.style };
                    const newBg = merged.background;
                    const prevBg = scene.style?.background;
                    const prevIsUrl =
                        typeof prevBg === "string" &&
                        prevBg.trim().startsWith("url(");
                    const newIsUrl =
                        typeof newBg === "string" &&
                        newBg.trim().startsWith("url(");
                    if (
                        newIsUrl &&
                        typeof prevBg === "string" &&
                        prevBg.trim() !== "" &&
                        !prevIsUrl
                    ) {
                        merged.backgroundFallback = prevBg;
                    }
                    scene.style = merged;
                }
                for (const [key, value] of Object.entries(updates)) {
                    if (key === "style") continue;
                    if (value !== undefined)
                        (scene as unknown as Record<string, unknown>)[key] =
                            value;
                }
            });
        },

        deleteScene: (sceneId) => {
            set((state) => {
                state.scenes = state.scenes.filter((s) => s.id !== sceneId);
                state.chapters = normalizeChapters(
                    state.scenes,
                    removeSceneFromAllChapters(state.chapters, sceneId)
                );
                state.metadata.numOfScenes = state.scenes.length;
                if (state.selectedSceneId === sceneId) {
                    state.selectedSceneId = state.scenes[0]?.id ?? null;
                    state.selection = { kind: "none" };
                }
            });
        },

        duplicateScene: (sceneId) => {
            set((state) => {
                const idx = state.scenes.findIndex((s) => s.id === sceneId);
                if (idx === -1) return;
                const source = state.scenes[idx];
                const dup: LomdaScene = {
                    ...source,
                    id: uuid(),
                    order: idx + 1,
                    elements: source.elements?.map((el) => ({
                        ...el,
                        id: uuid(),
                    })),
                    interactionsAfter: source.interactionsAfter?.map((i) => ({
                        ...i,
                        id: uuid(),
                    })),
                };
                state.scenes.splice(idx + 1, 0, dup);
                state.chapters = normalizeChapters(
                    state.scenes,
                    insertSceneAfter(state.chapters, dup.id, sceneId)
                );
                resortScenesByChapters(state);
                state.metadata.numOfScenes = state.scenes.length;
                state.selectedSceneId = dup.id;
                state.selection = { kind: "none" };
            });
        },

        reorderScenes: (fromIndex, toIndex) => {
            set((state) => {
                if (fromIndex === toIndex) return;
                const scenes = state.scenes;
                if (
                    fromIndex < 0 ||
                    fromIndex >= scenes.length ||
                    toIndex < 0 ||
                    toIndex >= scenes.length ||
                    state.chapters.length === 0
                )
                    return;
                const moved = scenes[fromIndex];
                const newFlat = [...scenes];
                newFlat.splice(fromIndex, 1);
                newFlat.splice(toIndex, 0, moved);

                let targetChapterId: string;
                let beforeSceneId: string | undefined;
                if (toIndex === 0) {
                    targetChapterId = state.chapters[0].id;
                    beforeSceneId = state.chapters[0].sceneIds[0];
                } else {
                    const neighborAbove = newFlat[toIndex - 1];
                    const host =
                        state.chapters.find((c) =>
                            c.sceneIds.includes(neighborAbove.id)
                        ) ?? state.chapters[state.chapters.length - 1];
                    targetChapterId = host.id;
                    const i = host.sceneIds.indexOf(neighborAbove.id);
                    beforeSceneId = host.sceneIds[i + 1];
                }
                state.chapters = normalizeChapters(
                    state.scenes,
                    moveSceneToChapterUtil(
                        state.chapters,
                        moved.id,
                        targetChapterId,
                        beforeSceneId
                    )
                );
                resortScenesByChapters(state);
            });
        },

        // ── elements ──
        addElement: (sceneId, element) => {
            set((state) => {
                const scene = findScene(state.scenes, sceneId);
                if (!scene) return;
                getElements(scene).push(element);
                state.selection = { kind: "element", elementId: element.id };
            });
        },

        updateElement: (sceneId, elementId, patch) => {
            set((state) => {
                const scene = findScene(state.scenes, sceneId);
                if (!scene) return;
                const el = getElements(scene).find((e) => e.id === elementId);
                if (!el) return;
                Object.assign(el, patch);
            });
        },

        updateElementTransform: (sceneId, elementId, transform) => {
            set((state) => {
                const scene = findScene(state.scenes, sceneId);
                if (!scene) return;
                const el = getElements(scene).find((e) => e.id === elementId);
                if (el) el.transform = transform;
            });
        },

        updateElementMedia: (sceneId, elementId, patch) => {
            set((state) => {
                const scene = findScene(state.scenes, sceneId);
                if (!scene) return;
                const el = getElements(scene).find((e) => e.id === elementId);
                if (el && isMediaElement(el)) Object.assign(el.media, patch);
            });
        },

        setTextElementHtml: (sceneId, elementId, html) => {
            set((state) => {
                const scene = findScene(state.scenes, sceneId);
                if (!scene) return;
                const el = getElements(scene).find((e) => e.id === elementId);
                if (el && el.kind === "text") el.html = html;
            });
        },

        setTextVerticalAlign: (sceneId, elementId, verticalAlign) => {
            set((state) => {
                const scene = findScene(state.scenes, sceneId);
                if (!scene) return;
                const el = getElements(scene).find((e) => e.id === elementId);
                if (el && el.kind === "text") el.verticalAlign = verticalAlign;
            });
        },

        setAvatarConfig: (sceneId, elementId, avatar) => {
            set((state) => {
                const scene = findScene(state.scenes, sceneId);
                if (!scene) return;
                const el = getElements(scene).find((e) => e.id === elementId);
                if (el && el.kind === "avatar") el.avatar = avatar;
            });
        },

        setAvatarFullScreen: (sceneId, elementId, fullScreen) => {
            // Snapshot the live (non-draft) floating layout before mutating, so
            // toggling back restores it exactly. Reads from `get()` to avoid
            // capturing revoked immer drafts. zIndex isn't tracked — avatars
            // always paint on top (see `paintZIndex`), matching the render.
            if (fullScreen) {
                const el = findScene(get().scenes, sceneId)?.elements?.find(
                    (e) => e.id === elementId
                );
                if (el?.kind === "avatar") {
                    avatarFloatSnapshots.set(elementId, {
                        transform: { ...el.transform },
                        avatar: structuredClone(el.avatar),
                    });
                }
            }

            set((state) => {
                const scene = findScene(state.scenes, sceneId);
                if (!scene) return;
                const el = getElements(scene).find((e) => e.id === elementId);
                if (!el || el.kind !== "avatar") return;

                if (fullScreen) {
                    el.transform = { ...FULLSCREEN_BOX };
                    el.avatar = {
                        ...el.avatar,
                        aspectRatio: "16:9",
                        style: {
                            ...(el.avatar.style ?? {}),
                            borderRadius: 0,
                            ringWidth: 0,
                            shadow: "none",
                        },
                    };
                    return;
                }

                const snap = avatarFloatSnapshots.get(elementId);
                avatarFloatSnapshots.delete(elementId);
                if (snap) {
                    el.transform = snap.transform;
                    el.avatar = snap.avatar;
                } else {
                    el.transform = defaultAvatarTransform();
                    el.avatar = {
                        ...el.avatar,
                        aspectRatio: "auto",
                        style: {
                            ...(el.avatar.style ?? {}),
                            borderRadius: 50,
                            ringWidth: 0,
                            shadow: "soft",
                        },
                    };
                }
            });
        },

        applyAvatarToAllScenes: (sceneId, elementId) => {
            set((state) => {
                const src = findScene(state.scenes, sceneId)?.elements?.find(
                    (e) => e.id === elementId
                );
                if (!src || src.kind !== "avatar") return;
                for (const scene of state.scenes) {
                    if (scene.id === sceneId) continue;
                    const els = getElements(scene);
                    const existing = els.find((e) => e.kind === "avatar");
                    if (existing && existing.kind === "avatar") {
                        existing.avatar = src.avatar;
                        existing.transform = { ...src.transform };
                        existing.zIndex = src.zIndex;
                    } else {
                        els.push(
                            createAvatarElement(
                                src.avatar,
                                { ...src.transform },
                                src.zIndex
                            )
                        );
                    }
                }
            });
        },

        setSceneAvatar: (sceneId, avatar) => {
            set((state) => {
                const scene = findScene(state.scenes, sceneId);
                if (!scene) return;
                const els = getElements(scene);
                const idx = els.findIndex((e) => e.kind === "avatar");
                if (avatar === null) {
                    if (idx >= 0) {
                        const removedId = els[idx].id;
                        els.splice(idx, 1);
                        if (
                            state.selection.kind !== "none" &&
                            state.selection.kind !== "scene" &&
                            state.selection.elementId === removedId
                        ) {
                            state.selection = { kind: "none" };
                        }
                    }
                    // Clear the legacy shadow too (see `removeElement`).
                    scene.avatar = null;
                    return;
                }
                const existing = els[idx];
                if (existing && existing.kind === "avatar") {
                    existing.avatar = avatar;
                    state.selection = {
                        kind: "element",
                        elementId: existing.id,
                    };
                } else {
                    const el = createAvatarElement(avatar);
                    els.push(el);
                    state.selection = { kind: "element", elementId: el.id };
                }
            });
        },

        removeElement: (sceneId, elementId) => {
            set((state) => {
                const scene = findScene(state.scenes, sceneId);
                if (!scene?.elements) return;
                const removed = scene.elements.find((e) => e.id === elementId);
                scene.elements = scene.elements.filter(
                    (e) => e.id !== elementId
                );
                // The avatar element shadows the legacy top-level `scene.avatar`;
                // clear it on delete so the render fallback (`readSceneAvatar`)
                // can't resurrect the removed avatar.
                if (removed?.kind === "avatar") scene.avatar = null;
                if (
                    state.selection.kind !== "none" &&
                    state.selection.kind !== "scene" &&
                    state.selection.elementId === elementId
                ) {
                    state.selection = { kind: "none" };
                }
            });
        },

        reorderElementZ: (sceneId, elementId, action) => {
            set((state) => {
                const scene = findScene(state.scenes, sceneId);
                if (!scene?.elements) return;
                const els = scene.elements;
                const sorted = [...els].sort((a, b) => a.zIndex - b.zIndex);
                const pos = sorted.findIndex((e) => e.id === elementId);
                if (pos === -1) return;
                if (action === "front") {
                    const [e] = sorted.splice(pos, 1);
                    sorted.push(e);
                } else if (action === "back") {
                    const [e] = sorted.splice(pos, 1);
                    sorted.unshift(e);
                } else if (action === "forward" && pos < sorted.length - 1) {
                    [sorted[pos], sorted[pos + 1]] = [
                        sorted[pos + 1],
                        sorted[pos],
                    ];
                } else if (action === "backward" && pos > 0) {
                    [sorted[pos], sorted[pos - 1]] = [
                        sorted[pos - 1],
                        sorted[pos],
                    ];
                }
                sorted.forEach((e, i) => {
                    const target = els.find((x) => x.id === e.id);
                    if (target) target.zIndex = i;
                });
            });
        },

        applyLayout: (sceneId, slots, align, verticalAlign) => {
            set((state) => {
                const scene = findScene(state.scenes, sceneId);
                if (!scene) return;
                const els = getElements(scene);
                const placed =
                    directionOf(state.metadata.language) === "rtl"
                        ? mirrorSlotsForRtl(slots)
                        : slots;
                if (placed.text) {
                    const textEl = els.find((e) => e.kind === "text");
                    if (textEl && textEl.kind === "text") {
                        textEl.transform = { ...placed.text };
                        if (align) {
                            textEl.html = setContentAlignment(
                                textEl.html,
                                align
                            );
                        }
                        if (verticalAlign) textEl.verticalAlign = verticalAlign;
                    }
                }
                if (placed.avatar) {
                    const avatarEl = els.find((e) => e.kind === "avatar");
                    if (avatarEl) avatarEl.transform = { ...placed.avatar };
                }
                if (placed.media) {
                    const mediaEls = els.filter((e) => e.kind === "media");
                    const tiles = tileRegion(placed.media, mediaEls.length);
                    mediaEls.forEach((el, i) => {
                        if (tiles[i]) el.transform = tiles[i];
                    });
                }
            });
        },

        // ── chapters ──
        renameChapter: (chapterId, title) =>
            writeChapters(set, (chs) =>
                renameChapterUtil(chs, chapterId, title)
            ),
        moveSceneToPreviousChapter: (sceneId) =>
            writeChapters(set, (chs) =>
                moveSceneToPreviousChapterUtil(chs, sceneId)
            ),
        moveSceneToNextChapter: (sceneId) =>
            writeChapters(set, (chs) =>
                moveSceneToNextChapterUtil(chs, sceneId)
            ),
        isolateSceneAsChapter: (sceneId) =>
            writeChapters(set, (chs) =>
                isolateSceneAsChapterUtil(chs, sceneId)
            ),
        splitChapterAt: (sceneId) =>
            writeChapters(set, (chs) => splitAtUtil(chs, sceneId)),
        reorderChapters: (fromIndex, toIndex) =>
            writeChapters(set, (chs) =>
                reorderChaptersUtil(chs, fromIndex, toIndex)
            ),
        moveSceneToChapter: (sceneId, targetChapterId, beforeSceneId) =>
            writeChapters(set, (chs) =>
                moveSceneToChapterUtil(
                    chs,
                    sceneId,
                    targetChapterId,
                    beforeSceneId
                )
            ),

        // ── interactions ──
        addInteractionToGap: (gapId, input) => {
            const newId = uuid();
            set((state) => {
                const scene = findScene(state.scenes, gapId);
                if (!scene) return;
                const current = scene.interactionsAfter ?? [];
                const insertAt = Math.max(
                    0,
                    Math.min(input.atOrder ?? current.length, current.length)
                );
                const { atOrder: _ignored, ...rest } = input;
                const next = {
                    ...rest,
                    id: newId,
                    order: insertAt,
                } as SceneInteraction;
                const arr = [
                    ...current.slice(0, insertAt),
                    next,
                    ...current.slice(insertAt),
                ];
                scene.interactionsAfter = renumber(arr);
            });
            return newId;
        },

        updateInteractionInGap: (gapId, interactionId, updates) => {
            set((state) => {
                const scene = findScene(state.scenes, gapId);
                if (!scene?.interactionsAfter) return;
                scene.interactionsAfter = renumber(
                    scene.interactionsAfter.map((i) =>
                        i.id === interactionId
                            ? ({ ...i, ...updates, id: i.id } as SceneInteraction)
                            : i
                    )
                );
            });
        },

        deleteInteractionFromGap: (gapId, interactionId) => {
            set((state) => {
                const scene = findScene(state.scenes, gapId);
                if (!scene?.interactionsAfter) return;
                scene.interactionsAfter = renumber(
                    scene.interactionsAfter.filter((i) => i.id !== interactionId)
                );
            });
        },

        moveInteractionToGap: ({ interactionId, toGapId, toOrder }) => {
            set((state) => {
                let found: SceneInteraction | null = null;
                for (const scene of state.scenes) {
                    const hit = scene.interactionsAfter?.find(
                        (i) => i.id === interactionId
                    );
                    if (hit) {
                        found = { ...hit };
                        scene.interactionsAfter = renumber(
                            scene.interactionsAfter!.filter(
                                (i) => i.id !== interactionId
                            )
                        );
                        break;
                    }
                }
                if (!found) return;
                const dest = findScene(state.scenes, toGapId);
                if (!dest) return;
                const items = dest.interactionsAfter ?? [];
                const insertAt = Math.max(
                    0,
                    Math.min(toOrder ?? items.length, items.length)
                );
                const arr = [
                    ...items.slice(0, insertAt),
                    { ...found, order: insertAt },
                    ...items.slice(insertAt),
                ];
                dest.interactionsAfter = renumber(arr);
            });
        },

        // ── metadata / bulk ──
        updateMetadata: (updater) => {
            set((state) => {
                state.metadata = updater(state.metadata);
            });
        },

        setLomdaName: (name) => {
            set((state) => {
                state.lomdaName = name;
            });
        },
    };
}

/** Apply a chapter transform, normalize, and resort scenes to match if order changed. */
function writeChapters(
    set: EditorSet,
    updater: (
        chapters: import("@/features/lomdaEditorV2/lib/lomdaTypes").LomdaChapter[]
    ) => import("@/features/lomdaEditorV2/lib/lomdaTypes").LomdaChapter[]
) {
    set((state) => {
        const nextChapters = normalizeChapters(
            state.scenes,
            updater(state.chapters)
        );
        const flattened = flattenChapterSceneIds(nextChapters);
        const current = state.scenes.map((s) => s.id);
        let orderChanged = flattened.length !== current.length;
        if (!orderChanged) {
            for (let i = 0; i < flattened.length; i++) {
                if (flattened[i] !== current[i]) {
                    orderChanged = true;
                    break;
                }
            }
        }
        state.chapters = nextChapters;
        if (orderChanged) resortScenesByChapters(state);
    });
}
